import { action, computed, observable, toJS } from 'mobx';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import { DEVICE_FILTER_FIELD_KEYS, DeviceFilterFieldKey } from '@kentik/ui-shared/device/deviceTableFilterFields';
import { isAssetTagFilterKey } from '@kentik/ui-shared/labels/lib';
import PagedCollection from 'core/model/PagedCollection';
import type { FetchOptions } from 'core/model/Collection';
import api from 'core/util/api';
import { deviceIndex } from 'app/util/lookups/deviceIndex';
import DeviceModel from './DeviceModel';
import $setup from '../$setup';

export const DEVICES_EXPORT_STATE_PARAM = 'tableState';

export interface DeviceTableExportState {
  filters: Record<string, unknown>;
  fieldsUsingAndLogic: DeviceFilterFieldKey[];
  sorting?: { field: string; direction?: 'asc' | 'desc' };
  groupBy: string | null;
}

class DeviceTableCollection extends PagedCollection {
  @observable
  pageState = {
    offset: 0,
    pageCount: 1,
    currentPage: 1,
    perPage: 50,
    totalCount: 0,
    pageCountLoaded: false,
    groupedDataFracturedKey: undefined
  };

  get model() {
    return DeviceModel;
  }

  // Base URL for bulk operations and model read/writes
  get url(): string {
    return '/api/ui/devices';
  }

  get urlPaths(): { destroy?: string; fetch?: string; update?: string; pagedFetch?: string } {
    return {
      pagedFetch: '/api/ui/devices/table'
    };
  }

  protected get queuedFetchKey(): string {
    return 'DeviceTableCollection';
  }

  get jsonRoot() {
    return 'data';
  }

  get defaultSortState(): { field: 'device_name'; direction: 'asc' } {
    return { field: 'device_name', direction: 'asc' };
  }

  get secondarySort() {
    return this.defaultSortState;
  }

  serverSortableFields = [
    'id',
    'device_name',
    'site_name',
    'site_market_name',
    'plan_name',
    'vendor',
    'model',
    'os_name',
    'os_version',
    'device_subtype',
    'serial_number',
    'ip_address',
    'statusDisplay',
    'deviceClass',
    'flowType',
    'percentClassified',
    'cpu_util',
    'memory_util',
    'traffic',
    'temperature_last',
    'rawFps',
    'downSampledFps',
    'stats.RibPrefixes',
    'stats.RibPrefixes6',
    'agent_snmp_credential_name',
    'sending_ips'
  ];

  @computed
  public get sortFieldIsServerSortable(): boolean {
    return !!(
      this.sortState.field &&
      (this.serverSortableFields.includes(this.sortState.field) || isAssetTagFilterKey(this.sortState.field))
    );
  }

  // FILTERING

  private static readonly defaultServerFilter: Record<string, unknown> = { isArchived: true };

  @observable.ref
  serverFilter: Record<string, unknown> = DeviceTableCollection.defaultServerFilter;

  // Which canSwitchLogic fields should use AND vs OR, kept separate from `serverFilter`'s own keys.
  @observable.ref
  fieldsUsingAndLogic: Set<DeviceFilterFieldKey> = new Set();

  // Prevent stale filters. The base resetState() does not reset these subclass observables.
  @action
  resetState(): void {
    this.serverFilter = DeviceTableCollection.defaultServerFilter;
    this.fieldsUsingAndLogic = new Set();
    super.resetState();
  }

  private static stripDeadFilters(filter: Record<string, unknown>): Record<string, unknown> {
    return omitBy(
      filter,
      (value) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
    );
  }

  private toServerFilter(filter: Record<string, unknown>): Record<string, unknown> {
    const cleanedFilters = DeviceTableCollection.stripDeadFilters(filter);
    const labelFilterValue = cleanedFilters[DEVICE_FILTER_FIELD_KEYS.LABEL];

    // Handle AND/OR logic unique to label filtering
    if (Array.isArray(labelFilterValue)) {
      cleanedFilters[DEVICE_FILTER_FIELD_KEYS.LABEL] = {
        ids: labelFilterValue,
        logic: this.fieldsUsingAndLogic.has(DEVICE_FILTER_FIELD_KEYS.LABEL) ? 'and' : 'or'
      };
    }

    return cleanedFilters;
  }

  getServerFilter(): Record<string, unknown> {
    return this.toServerFilter(toJS(this.serverFilter));
  }

  @action
  setServerFilter(
    filter: Record<string, unknown> & { fieldsUsingAndLogic?: Set<DeviceFilterFieldKey> }
  ): Promise<unknown> {
    const { fieldsUsingAndLogic = this.fieldsUsingAndLogic, ...rest } = filter;
    const nextFilter = DeviceTableCollection.stripDeadFilters(rest);
    const prevFilter = DeviceTableCollection.stripDeadFilters(toJS(this.serverFilter));
    if (isEqual(nextFilter, prevFilter) && isEqual(fieldsUsingAndLogic, this.fieldsUsingAndLogic)) {
      return Promise.resolve();
    }
    this.serverFilter = { ...nextFilter };
    this.fieldsUsingAndLogic = fieldsUsingAndLogic;
    this.resetPageState();
    return this.queuedFetch({ force: true });
  }

  // EXPORT

  // Serializable snapshot of the filter/sort/group state, so a headless PDF export can reproduce this view.
  getExportState(): DeviceTableExportState {
    const { field, direction } = this.sortState;
    return {
      filters: toJS(this.serverFilter),
      fieldsUsingAndLogic: Array.from(this.fieldsUsingAndLogic),
      sorting: field ? { field, direction } : undefined,
      groupBy: typeof this.groupBy === 'string' ? this.groupBy : null
    };
  }

  // Applies a getExportState() snapshot without fetching; callers fetch afterwards. Sort fields that
  // aren't server sortable must be re-sorted locally after the fetch.
  @action
  applyExportState(state: Partial<DeviceTableExportState>): void {
    const { filters, fieldsUsingAndLogic, sorting, groupBy } = state;
    if (filters && typeof filters === 'object' && !Array.isArray(filters)) {
      this.serverFilter = { ...filters };
    }
    if (Array.isArray(fieldsUsingAndLogic)) {
      this.fieldsUsingAndLogic = new Set(fieldsUsingAndLogic);
    }
    if (typeof sorting?.field === 'string' && sorting.field) {
      this.setSortState(sorting.field as never, sorting.direction === 'desc' ? 'desc' : 'asc');
    }
    if (typeof groupBy === 'string') {
      this.groupBy = groupBy as never;
    }
  }

  // SORTING

  serverSortFn = () => {
    this.resetPageState();
    return this.queuedFetch({ force: true });
  };

  private getSortingParam(): { field?: string; direction?: 'asc' | 'desc' } | undefined {
    const { field, direction } = this.sortState;
    return field ? { field, direction } : undefined;
  }

  // SELECTION & GROUPING

  @action
  selectAll() {
    this.selected = this.isAllSelected ? null : this.models.filter((model) => model.isSelectable);
  }

  // Overrides Collection's base group() — that version re-fetches via loadMoreItems/this.fetch,
  // which uses a different request shape than this collection's paged/queued fetch.
  @action
  group(value: (string & keyof DeviceModel) | null): void {
    const nextGroupBy = value || null;
    const changed = this.groupBy !== nextGroupBy;
    this.groupBy = nextGroupBy;
    if (changed) {
      this.resetPageState();
      this.queuedFetch({ force: true });
    }
  }

  // PAGINATION

  async goToPage({
    page = 1,
    perPage,
    refetchTotals = true,
    preserveSelection = false
  }: {
    page?: number;
    perPage?: number;
    refetchTotals?: boolean;
    preserveSelection?: boolean;
  } = {}): Promise<unknown> {
    const nextPage = Math.max(page, 1);
    const nextPerPage = Math.max(perPage ?? this.pageState.perPage, 1);
    const offset = (nextPage - 1) * nextPerPage;

    this.setPageState({ currentPage: nextPage, perPage: nextPerPage, offset });

    return this.queuedFetch({ includeTotalCount: refetchTotals, preserveSelection }).then((result) => {
      if (preserveSelection) {
        const selectedIds = this.selectedArray.map((model) => model.id);
        this.selected = selectedIds.map((id) => this.get(id)).filter((model): model is DeviceModel => !!model);
      }
      return result;
    });
  }

  // FETCHING

  async fetch(options: FetchOptions<DeviceModel> = {}) {
    return this.queuedFetch({ preserveSelection: options.preserveSelection }).then((result) => {
      if (options.preserveSelection) {
        const selectedIds = this.selectedArray.map((model) => model.id);
        this.selected = selectedIds.map((id) => this.get(id)).filter((model): model is DeviceModel => !!model);
      }
      return result;
    });
  }

  async queuedFetch(options: Record<string, unknown> = {}) {
    const {
      data = {},
      fetchUrl,
      fetchMethod,
      includeTotalCount = true
    } = options as {
      data?: Record<string, unknown>;
      fetchUrl?: string;
      fetchMethod?: 'get' | 'post';
      includeTotalCount?: boolean;
    };
    const { perPage, offset } = this.pageState;
    const defaultPagination = { limit: perPage, offset, includeTotalCount };

    return super.queuedFetch({
      ...options,
      fetchUrl: fetchUrl || this.urlPaths.pagedFetch,
      fetchMethod: fetchMethod || 'post',
      data: {
        filters: this.getServerFilter(),
        sorting: this.getSortingParam(),
        groupBy: typeof this.groupBy === 'string' ? this.groupBy : null,
        pagination: defaultPagination,
        ...data
      }
    });
  }

  @action
  // @ts-expect-error needs proper conversion to typed PagedCollection
  processData(response: {
    data?: unknown[];
    pagination?: { total_count?: number | null };
    groupedDataFracturedKey?: string;
  }) {
    const { totalCount, pageCount } = this.pageState;
    // @ts-expect-error needs proper conversion to typed PagedCollection
    const result = super.processData(response);
    if (response.pagination?.total_count == null) {
      // server didn't recount (total_count omitted); keep the last known total instead of the reset value
      this.setPageState({ totalCount, pageCount });
    }
    this.setPageState({ pageCountLoaded: true, groupedDataFracturedKey: response.groupedDataFracturedKey });
    return result;
  }

  // BULK OPERATIONS

  async bulkDelete(options = {}) {
    const { selectedArray } = this as unknown as { selectedArray: DeviceModel[] };
    return super
      .bulkDelete({ ...options, removeFromCollection: false })
      .then(async (result: { hasErrors?: boolean; errors?: { deviceId: string | number }[] }) => {
        const deleted = selectedArray?.filter(
          (device: DeviceModel) =>
            !result.hasErrors || !result.errors?.find((errDevice) => errDevice.deviceId === device.id)
        );

        // ?? [] so a missing selection never falls through to invalidating every record
        deviceIndex.invalidate(deleted?.map((device) => device.id) ?? []);

        deleted?.forEach((model: DeviceModel) => {
          if (model.isArchived) {
            this.remove(model.id);
          } else {
            model.set('device_status', 'D');
          }
        });

        this.refreshDeviceStores();
        await this.queuedFetch({ force: true });

        return result;
      });
  }

  async bulkUpdateSite(url: string, data: { value: string | number; ids: (string | number)[] }) {
    this.setRequestStatus('updating');
    try {
      const {
        ids,
        name: site_name,
        site_market_id,
        site_market_name
      } = await api.post(`${url}/bulkUpdateSite`, { data });
      this.refreshDeviceStores();
      ids.forEach((id: string) => {
        this.get(id)?.safeSet({ site_id: data.value, site_market_id, site_market_name, site_name });
      });
      // includes devices that aren't on the current page
      deviceIndex.invalidate(ids);
    } finally {
      this.setRequestStatus(null);
    }
  }

  @action
  async bulkUpdatePlan(url: string, data: { value: string | number; ids: (string | number)[] }) {
    this.setRequestStatus('updating');
    try {
      const { ids, plan_name } = await api.post(`${url}/bulkUpdatePlan`, { data });

      const decrements: Record<string, number> = {};
      ids.forEach((id: string) => {
        const prevPlan = this.get(id)?.get('plan_id') as string;
        decrements[prevPlan] = (decrements[prevPlan] || 0) + 1;
        this.get(id)?.safeSet({ plan_id: data.value, plan_name });
      });

      $setup.plans = $setup.plans.map((plan) => {
        if (String(plan.id) === String(data.value)) {
          plan.device_count += ids.length;
        }
        if (decrements[plan.id]) {
          plan.device_count -= decrements[plan.id];
        }
        return plan;
      });
    } finally {
      this.setRequestStatus(null);
    }
  }

  async bulkUpdate(
    name: 'plan_id' | 'site_id' | 'kentik_agent_snmp_options.agent_id',
    value: unknown
  ): Promise<void | null> {
    if (name === 'kentik_agent_snmp_options.agent_id') {
      return super.bulkUpdate(name, value);
    }
    const { url } = this;
    const { selectedIds } = this;
    if (!selectedIds?.length || (typeof value !== 'string' && typeof value !== 'number')) {
      return null;
    }
    const data = { ids: selectedIds, value };
    return name === 'site_id' ? this.bulkUpdateSite(url, data) : this.bulkUpdatePlan(url, data);
  }

  /**
   * Refreshes stores related to Devices, updating their contents after this collection performs bulk updates or deletes
   * @private
   */
  private refreshDeviceStores(): Promise<void> {
    // TODO we are going to kill off device summaries etc in future but we will still need to load $sites.collection etc here!
    return import('app/stores/site/$sites').then(({ default: $sites }) => $sites.refreshSites());
  }
}

export default DeviceTableCollection;
